import React from "react"
import { IconArrowRight } from "@tabler/icons-react"

/**
 * A Brilliant-style "how a word changes" visual: one form transforming into another, with each
 * significant letter highlighted like a word-guesser hint and an optional annotation under each side.
 * Used to teach the initial mutations (eclipsis, lenition) and any before/after form change.
 *
 * Letters are marked with a light bracket syntax so the same string works in static config and in
 * item data (no JSX authoring needed):
 *   [x]   highlight a letter (neutral, the letter in focus)      -> "[b]ord"
 *   [+x]  an ADDED letter (the new sound), shown green            -> "[+m]bord"
 *   [-x]  a SUPPRESSED letter (sound cancelled), muted + struck   -> "[+m][-b]ord"
 * Everything outside brackets is plain text. So eclipsis is `[b]ord` -> `[+m][-b]ord`: the m is added
 * and now sounded, the b is written but silent.
 *
 * @example
 * <WordTransform from="[b]ord" to="[+m][-b]ord" fromNote="table" toNote="/m/ ‘moo’" />
 */

export type SegmentRole = "base" | "mark" | "add" | "remove"

export interface Segment {
  text: string
  role: SegmentRole
}

// Match either a bracketed group ([+x] / [-x] / [x]) or a run of plain text.
const SEGMENT_RE = /\[([+-]?)([^\]]*)\]|([^[]+)/g

/** Parse a marked string into typed segments. Plain, un-bracketed text becomes one `base` segment. */
export function parseTransform(input: string): Segment[] {
  const segments: Segment[] = []
  let match: RegExpExecArray | null
  SEGMENT_RE.lastIndex = 0
  while ((match = SEGMENT_RE.exec(input)) !== null) {
    if (match[3] != null) {
      segments.push({ text: match[3], role: "base" })
    } else {
      const flag = match[1]
      const role: SegmentRole = flag === "+" ? "add" : flag === "-" ? "remove" : "mark"
      segments.push({ text: match[2], role })
    }
  }
  return segments
}

// Chip styling per role, echoing the word-guesser hint tiles (solid color, bold, mono).
const ROLE_CLASS: Record<Exclude<SegmentRole, "base">, string> = {
  mark: "bg-primary-500 text-white",
  add: "bg-green-600 text-white",
  remove: "bg-gray-200 text-gray-400 line-through",
}

const Chip: React.FC<{ segment: Segment }> = ({ segment }) => (
  <span
    data-role={segment.role}
    className={`mx-px inline-flex items-center justify-center rounded px-1 font-bold ${
      ROLE_CLASS[segment.role as Exclude<SegmentRole, "base">]
    }`}
  >
    {segment.text}
  </span>
)

/** One marked word, rendered as a kbd "code" box with its highlighted letters inline. */
export const TransformWord: React.FC<{ text: string; note?: string }> = ({ text, note }) => (
  <span className="inline-flex flex-col items-center gap-1">
    <kbd className="inline-flex items-center rounded-md border border-secondary-200 bg-secondary-50 px-1.5 py-1 font-mono text-base not-italic text-foreground shadow-none">
      {parseTransform(text).map((segment, index) =>
        segment.role === "base" ? (
          <span key={index}>{segment.text}</span>
        ) : (
          <Chip key={index} segment={segment} />
        ),
      )}
    </kbd>
    {note && <span className="text-xs text-muted-foreground">{note}</span>}
  </span>
)

export interface WordTransformProps {
  /** The starting form, in the bracket syntax (e.g. "[b]ord"). */
  from: string
  /** The resulting form (e.g. "[+m][-b]ord"). Omit to show a single annotated form. */
  to?: string
  /** Annotation under each side (the "why" / sound). */
  fromNote?: string
  toNote?: string
}

export const WordTransform: React.FC<WordTransformProps> = ({ from, to, fromNote, toNote }) => (
  <div className="flex flex-wrap items-center gap-3" data-testid="word-transform">
    <TransformWord text={from} note={fromNote} />
    {to && (
      <>
        <IconArrowRight size={18} className="shrink-0 text-muted-foreground" aria-label="becomes" />
        <TransformWord text={to} note={toNote} />
      </>
    )}
  </div>
)

/** A data-driven stack of transforms (from static config or item data). */
export const TransformList: React.FC<{ rows: WordTransformProps[] }> = ({ rows }) => (
  <div className="space-y-3" data-testid="transform-list">
    {rows.map((row, index) => (
      <WordTransform key={index} {...row} />
    ))}
  </div>
)

export default WordTransform
